<script lang="ts">
    import { onMount, onDestroy, tick, createEventDispatcher } from 'svelte';
    import { twMerge } from 'tailwind-merge';
    import { getPdfContext } from '../context/PdfContext';
    import { createOptimizedCanvas, calculateCanvasScale, calculateScale, setupCanvasSize, renderPdfPage, setElementSize } from '../utils/renderUtils';
    import type { PDFPageProxy } from 'pdfjs-dist';

    // Props
    export let pageNumber: number | undefined = undefined;
    let className: string = '';
    export { className as class };

    // Context
    const { documentStore, navigationStore } = getPdfContext();

    // DOM References
    let wrapperRef: HTMLDivElement;
    let canvasRef: HTMLCanvasElement;

    // State
    let page: PDFPageProxy | null = null;
    let mounted = false;
    let resizeFrame: number | null = null;
    const dispatch = createEventDispatcher<{ pageready: { wrapper: HTMLDivElement; pageNumber: number } }>();

    // 현재 페이지 번호 계산 (props 또는 context에서)
    $: currentPageNum = pageNumber ?? $navigationStore.currentPage;

    // 페이지 렌더링
    const renderPage = async () => {
        if (!mounted || !wrapperRef || !$documentStore.document) return;

        try {
            // PDF 페이지 가져오기
            page = await documentStore.getPage(currentPageNum);

            // 비동기 작업 중 컴포넌트가 언마운트되었을 수 있으므로 재확인
            if (!mounted || !wrapperRef) return;

            const baseViewport = page.getViewport({ scale: 1 });
            const parentWidth = wrapperRef.parentElement?.clientWidth || 800;
            const availableWidth = resolveAvailableWidth(wrapperRef, parentWidth);

            // 스케일 계산
            const scale = calculateScale(baseViewport.width, availableWidth);
            const scaledViewport = page.getViewport({ scale });

            // Canvas 생성
            const devicePixelRatio = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
            const canvasScale = calculateCanvasScale(devicePixelRatio);
            const result = createOptimizedCanvas(scaledViewport.width * canvasScale, scaledViewport.height * canvasScale, {
                enableGPU: true,
            });

            if (!result) {
                console.error('Failed to create canvas');
                return;
            }

            const { canvas, context } = result;

            // Canvas 크기 설정
            setupCanvasSize(canvas, scaledViewport.width, scaledViewport.height, canvasScale);

            // Context에 스케일 적용
            context.scale(canvasScale, canvasScale);

            // Wrapper 크기 설정
            setElementSize(wrapperRef, Math.floor(scaledViewport.width), Math.floor(scaledViewport.height));
            wrapperRef.dataset.documentPage = String(currentPageNum);
            wrapperRef.dataset.page = String(currentPageNum);
            wrapperRef.dataset.nativeWidth = String(scaledViewport.width);
            wrapperRef.dataset.nativeHeight = String(scaledViewport.height);
            wrapperRef.dataset.originalWidth = String(baseViewport.width);
            wrapperRef.dataset.originalHeight = String(baseViewport.height);
            wrapperRef.style.setProperty('--scale-factor', String(scale));
            wrapperRef.dataset.scaleFactor = String(scale);

            // DOM 추가 전에 PDF 페이지를 먼저 렌더링 (깜빡임 방지)
            await renderPdfPage(page, canvas, context, scale);

            // 렌더링 완료 후 컴포넌트가 여전히 마운트되어 있는지 재확인
            if (!mounted || !wrapperRef) return;

            // 렌더링 완료 후 canvas를 DOM에 추가
            canvas.dataset.page = String(currentPageNum);

            // 부드러운 전환을 위한 fade-in 효과
            canvas.style.opacity = '0';

            if (canvasRef) {
                canvasRef.replaceWith(canvas);
            } else {
                wrapperRef.appendChild(canvas);
            }
            canvasRef = canvas;

            // 다음 프레임에서 opacity 변경 (CSS transition 적용)
            requestAnimationFrame(() => {
                if (canvasRef) {
                    canvasRef.style.opacity = '1';
                }
            });

            // pageready 이벤트 전 최종 확인
            if (mounted && wrapperRef) {
                dispatch('pageready', { wrapper: wrapperRef, pageNumber: currentPageNum });
            }
        } catch (error) {
            console.error('Error rendering page:', error);
        }
    };

    // 마운트 시 렌더링
    onMount(async () => {
        mounted = true;
        await tick();
        await renderPage();
    });

    // 언마운트 시 정리
    onDestroy(() => {
        mounted = false;
        page?.cleanup();
        if (resizeFrame) {
            cancelAnimationFrame(resizeFrame);
            resizeFrame = null;
        }
    });

    // 페이지 번호 변경 시 재렌더링
    $: if (currentPageNum && mounted) {
        renderPage();
    }

    const handleResize = () => {
        if (!mounted) return;
        if (resizeFrame) {
            cancelAnimationFrame(resizeFrame);
        }
        resizeFrame = requestAnimationFrame(() => {
            resizeFrame = null;
            renderPage();
        });
    };

    const parseSize = (value: string | null | undefined): number | null => {
        if (!value || value === 'none' || value === 'auto') return null;
        const parsed = parseFloat(value);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    };

    const resolveAvailableWidth = (wrapper: HTMLElement | null, fallback: number): number => {
        if (!wrapper || typeof window === 'undefined') {
            return fallback;
        }

        let width = fallback;
        const styles = window.getComputedStyle(wrapper);
        const explicitWidth = parseSize(styles.width);
        if (explicitWidth) {
            width = explicitWidth;
        }

        const maxWidth = parseSize(styles.maxWidth);
        if (maxWidth) {
            width = Math.min(width, maxWidth);
        }

        const minWidth = parseSize(styles.minWidth);
        if (minWidth) {
            width = Math.max(width, minWidth);
        }

        return width || fallback;
    };
</script>

<div
    bind:this={wrapperRef}
    class={twMerge('pdf-page-wrapper relative mb-4 shrink-0', className)}
    data-page-number={currentPageNum}
    style="transform: translateZ(0); backface-visibility: hidden; will-change: transform;"
>
    <slot {currentPageNum} />
</div>

<svelte:window on:resize={handleResize} />

<style lang="postcss" global>
    // 외부에서 접근 가능한 클래스
  .pdf-page-wrapper {
        position: relative;
        background-color: white;
    }

    .pdf-page-wrapper canvas {
        display: block;
        width: 100%;
        height: 100%;
        transition: opacity 150ms ease-in;
    }
</style>
